import SettingsAddUserModal from "@/components/SettingsAddUserModal/SettingsAddUserModal.vue";
import settings_user_helpers, { settings_user_table_helpers } from "@/components/SettingsUserManagementTab/settings_user_management_helpers";

const users_url = process.env.VUE_APP_USER_MANAGEMENT_URL || process.env.VUE_APP_VIEW_USER_URL || "";
const deactivate_user_url = process.env.VUE_APP_DEACTIVATE_USER_URL || users_url;
const users_list_url = process.env.VUE_APP_USER_LIST_URL || "";
const create_user_url = process.env.VUE_APP_CREATE_USER_URL || "";
const roles_url = process.env.VUE_APP_ROLES_URL || "";
const client_app = process.env.VUE_APP_CLIENT_APP || "epfdsd";

export default {
    name: "SettingsUserManagementTab",

    components: {
        SettingsAddUserModal
    },

    props: {
        user_profile: {
            type: Object,
            default: null
        }
    },

    data() {
        return ({
            users: [],
            role_options: [],
            is_loading: false,
            is_loading_roles: false,
            is_saving: false,
            load_error: "",
            form_error: "",
            save_success: "",
            show_add_user_modal: false,
            users_url: users_url,
            deactivate_user_url: deactivate_user_url,
            users_list_url: users_list_url,
            create_user_url: create_user_url,
            roles_url: roles_url,
            client_app: client_app
        });
    },

    computed: {
        active_users_count() {
            return this.users.filter((user) => user.is_active).length;
        },

        current_user_id() {
            return this.user_profile?.id || null;
        },

        current_user_email() {
            return String(this.user_profile?.email || "").toLowerCase();
        }
    },

    watch: {
        ...(settings_user_table_helpers.watch || {}),
        user_profile: {
            deep: true,
            handler(next_profile) {
                if (next_profile && !this.users.length && !this.is_loading) {
                    this.set_users(this.get_fallback_users());
                }
            }
        }
    },

    async mounted() {
        await this.load_initial_data();
    },

    beforeUnmount() {
        settings_user_table_helpers.beforeUnmount?.call(this);
    },

    methods: {
        ...(settings_user_table_helpers.methods || {}),
        ...settings_user_helpers,

        set_users(next_users, should_rebuild = true) {
            this.destroy_tables();
            this.users = next_users;

            if (should_rebuild) {
                this.$nextTick(() => {
                    this.initialize_tables_when_ready();
                });
            }
        },

        open_add_user() {
            this.form_error = "";
            this.save_success = "";
            this.show_add_user_modal = true;
        },

        open_edit_user(user) {
            this.form_error = "";
            this.save_success = "";
            sessionStorage.setItem("edit_user_profile", JSON.stringify(user || {}));
            this.$router.push({
                path: "/edit_profile",
                query: {
                    user_id: user.id,
                    return_to: "user-management"
                }
            });
        },

        close_add_user_modal() {
            this.show_add_user_modal = false;
            this.form_error = "";
        },

        validate_user_payload(payload) {
            if (!payload.first_name || !payload.last_name || !payload.email) {
                return "First name, last name, and email are required.";
            }

            return "";
        },

        save_user_locally(form_payload, payload) {
            this.set_users([
                ...this.users,
                {
                    id: `local-${Date.now()}`,
                    first_name: payload.first_name,
                    last_name: payload.last_name,
                    username: payload.username,
                    email: payload.email,
                    phone_number: payload.phone_number,
                    role: form_payload.role?.name || "",
                    role_id: payload.role_id,
                    is_active: payload.is_active
                }
            ]);
        },

        async persist_user_status(user, next_user) {
            const endpoint = this.get_user_status_endpoint(user, next_user);

            if (!endpoint) {
                this.set_users(this.replace_user_in_list(user, next_user));
                return;
            }

            await this.save_user_status_to_server(endpoint, next_user);
            this.users = this.replace_user_in_list(user, next_user);
            await this.load_users();
        },

        guard_current_user_status_change(user) {
            if (this.is_current_user(user) && user.is_active) {
                this.load_error = "You cannot deactivate your own account.";
                return false;
            }

            return true;
        },

        // "12 Sep 2026, 09:14" for last login, "12 Sep 2026" for joined
        format_user_date(value, include_time = false) {
            if (!value) return "Never";

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return String(value);

            return date.toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                ...(include_time ? { hour: "2-digit", minute: "2-digit" } : {})
            });
        },

        // Lets DataTables sort the date columns by time rather than by label
        get_date_order(value) {
            const time = new Date(value || "").getTime();
            return Number.isNaN(time) ? 0 : time;
        },

        get_status_success_message(next_user) {
            return next_user.is_active ? "User activated." : "User deactivated.";
        },

        async toggle_user_status(user) {
            if (!this.guard_current_user_status_change(user)) return;

            this.is_saving = true;
            this.load_error = "";
            this.save_success = "";

            const next_user = {
                ...user,
                is_active: !user.is_active
            };

            try {
                await this.persist_user_status(user, next_user);
                this.save_success = this.get_status_success_message(next_user);
            } catch (error) {
                this.load_error = error.message || "Unable to update user status.";
            } finally {
                this.is_saving = false;
            }
        },

    }
};
